import { SignalWithAuditDto } from "@/types";
import { CreateSignalInput, UpdateSignalInput } from "@/schemas";
import { RelatedObjectType } from "@/enums";
import { logger } from "@/lib/logger";
import { apiClient } from "@/services/api";

export type CreateSignalRequest = CreateSignalInput;
export type UpdateSignalRequest = UpdateSignalInput;

/**
 * Creates a new signal by sending the provided data to the backend.
 *
 * @param data - The data required to create a new signal.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createSignal(
  data: CreateSignalRequest,
  accountId: string
): Promise<SignalWithAuditDto> {
  try {
    const response = await fetch(`/api/accounts/${accountId}/signals`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }

    return await response.json();
  } catch (error) {
    logger.error("Error creating signal", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}

/**
 * Fetches all signals for a specific account by its ID.
 *
 * @param accountId - The ID of the account to fetch signals for
 * @returns A list of signals.
 * @throws An error if the request fails.
 */
export async function fetchSignalsByAccountId(
  accountId: string
): Promise<SignalWithAuditDto[]> {
  try {
    const response = await fetch(`/api/accounts/${accountId}/signals`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching signals", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}

/**
 * Fetches the top 3 most recently added growth signals for a specific account.
 * Orders by creation date (most recent first) and limits to 3 results.
 *
 * @param accountId - The ID of the account to fetch top growth signals for
 * @returns A list of top 3 growth signals.
 * @throws An error if the request fails.
 */
export async function fetchTopGrowthSignalsByAccountId(
  accountId: string
): Promise<SignalWithAuditDto[]> {
  try {
    const response = await fetch(
      `/api/accounts/${accountId}/signals?topGrowth=true`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-store",
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching top growth signals", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}

/**
 * Fetches the top 3 most recently added risk signals for a specific account.
 * Orders by creation date (most recent first) and limits to 3 results.
 *
 * @param accountId - The ID of the account to fetch top risk signals for
 * @returns A list of top 3 risk signals.
 * @throws An error if the request fails.
 */
export async function fetchTopRiskSignalsByAccountId(
  accountId: string
): Promise<SignalWithAuditDto[]> {
  try {
    const response = await fetch(
      `/api/accounts/${accountId}/signals?topRisk=true`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-store",
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }

    return await response.json();
  } catch (error) {
    logger.error("Error fetching top risk signals", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}

/**
 * Fetches a single signal by ID.
 *
 * @param signalId - The ID of the signal to fetch
 * @returns The signal data with audit information.
 * @throws An error if the request fails.
 */
export async function fetchSignalById(
  signalId: string
): Promise<SignalWithAuditDto> {
  try {
    return await apiClient<SignalWithAuditDto>(`/api/signals/${signalId}`, {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logger.error("Error fetching signal", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}

/**
 * Updates an existing signal by sending the provided data to the backend.
 *
 * @param signalId - The ID of the signal to update
 * @param data - The data to update the signal with.
 * @returns A promise that resolves with the updated signal data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateSignal(
  signalId: string,
  data: UpdateSignalRequest
): Promise<SignalWithAuditDto> {
  try {
    const response = await fetch(`/api/signals/${signalId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }

    return await response.json();
  } catch (error) {
    logger.error("Error updating signal", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}

/**
 * Fetches related objects for a signal by type(s).
 *
 * @param signalId - The ID of the signal
 * @param types - Array of related object types (e.g., ["ACTIVITY", "GOAL"])
 * @returns An object with arrays of related objects grouped by type.
 * @throws An error if the request fails.
 */
export async function fetchSignalRelatedObjects(
  signalId: string,
  types: RelatedObjectType[]
): Promise<Record<string, unknown[]>> {
  try {
    if (!signalId || !types.length) return {};
    const params = new URLSearchParams();
    types.forEach((t) => params.append("type", t));
    const response = await fetch(
      `/api/signals/${signalId}/related-objects?${params.toString()}`
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }
    return await response.json();
  } catch (error) {
    logger.error("Error fetching signal related objects", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}

/**
 * Deletes a signal by its ID.
 * @param signalId - The ID of the signal to delete
 * @returns A promise that resolves when the signal is deleted.
 * @throws An error if the request fails or if the signal is not found.
 */
export async function deleteSignal(signalId: string): Promise<void> {
  try {
    const response = await fetch(`/api/signals/${signalId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }
  } catch (error) {
    logger.error("Error deleting signal", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}

/**
 * Fetches all signals across all accounts for global dashboard statistics.
 * This aggregates signals from all accounts the user has access to.
 *
 * @returns A list of all signals across all accounts.
 * @throws An error if the request fails.
 */
export async function fetchAllSignals(): Promise<SignalWithAuditDto[]> {
  try {
    // Fetch all signals across all accounts in a single request
    const response = await fetch("/api/signals/all", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch all signals");
    }

    return response.json();
  } catch (error) {
    logger.error("Error fetching all signals", error, {
      context: { source: "service", module: "signalsServices" },
    });
    throw error;
  }
}
